/**
 * Bulk self-schedule mutation.
 * POST /api_ats/v1/ats_schedules/bulk
 *
 * Two backend response shapes (controlled by `is_bulk_activity` in
 * the payload — the wizard always sends `true`, see
 * `payload/mapBulkSelfScheduleContext.ts`):
 *
 *   - `is_bulk_activity: true`  → `{ id: <bulk_activity_id> }`. The
 *     server queues a `BulkActivities::BulkActivityWorker` and the
 *     `BulkOperationQueue` polls for progress via the bulk-activities
 *     fetch the caller triggers. Async path — the only one wired here.
 *   - `is_bulk_activity: false` → `{ data: { ats_applications: [...] },
 *     errors: [] }`. Sync path — kept on the legacy
 *     `KInterviewScheduler` path; the new wizard does not enable it.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { asAtsClientBody, unwrapAtsEnvelope } from '@/api/ats/atsEnvelope';
import { convertToFormData } from '@/lib/util';

export interface CreateBulkSelfScheduleVariables {
  payload: Record<string, unknown>;
}

export interface BulkSelfScheduleAsyncResponse {
  id?: number | string;
}

export function useCreateBulkSelfSchedule() {
  const queryClient = useQueryClient();

  return useMutation<
    BulkSelfScheduleAsyncResponse,
    Error,
    CreateBulkSelfScheduleVariables
  >({
    mutationFn: async ({ payload }) => {
      const raw = asAtsClientBody<unknown>(
        await http.post('/ats_schedules/bulk', convertToFormData(payload))
      );
      return unwrapAtsEnvelope<BulkSelfScheduleAsyncResponse>(raw);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-interview'] });
    },
  });
}
